import { useAuth } from '../../../../../hooks'
import React, { useEffect, useMemo, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import {
	Button,
	Checkbox,
	IconButton,
	Input,
	MultiValueInput,
} from '../../../../ui'
import DatePicker from '../../../../ui/DatePicker'
import {
	DocumentData,
	CurrentDocumentData,
} from '../../../../../lib/api/types/document.interfaces'
import {
	useClassifyFile,
	useExactFileMetadata,
	useFile,
	useMetadata,
	useUpdateMetadata,
} from '../../../../../hooks/api/documentHooks'
import Loader from '../../../../common/Loader'
import {
	Cancel,
	Close,
	Done,
	EditOutlined,
	ErrorOutline,
	Refresh,
	Search,
} from '@mui/icons-material'
import { useCategory } from '../../../../../hooks/api/categoryHooks'
import { CategoryItemField } from '../../../../../lib/api/types/organization.interfaces'
import { processingStatuses } from '../../../../../lib/utils/formatDocument'
import { useUserDateFormat } from '../../../../../hooks/useUserDateFormat'
import dayjs from 'dayjs'
import { useIsMutating } from '@tanstack/react-query'

interface DocumentMetadataForm {
	processingStatus: number
	[key: string]: any
}

interface AiFormTabProps {
	fileInfo: DocumentData | CurrentDocumentData | null
	isEditMode: boolean
	onToggleEditMode: React.Dispatch<React.SetStateAction<boolean>>
}

const AiFormTab: React.FC<AiFormTabProps> = ({
	fileInfo,
	isEditMode,
	onToggleEditMode,
}) => {
	const { isAuthenticated } = useAuth()
	const [searchQuery, setSearchQuery] = useState('')
	const { formatDate } = useUserDateFormat()

	const methods = useForm<DocumentMetadataForm>()
	const {
		handleSubmit,
		reset,
		setValue,
		control,
		formState: { isDirty },
	} = methods

	const { mutateAsync: classifyFile, isPending: isClassifying } =
		useClassifyFile()
	const { mutateAsync: extractMetadata, isPending: isExacting } =
		useExactFileMetadata()
	const { mutateAsync: updateMetadata, isPending: isUpdating } =
		useUpdateMetadata()

	const { data: fileData, isLoading: isFileLoading } = useFile(
		{ id: fileInfo?.id ?? ''},
		{ enabled: Boolean(fileInfo?.id && !fileInfo?.subcategory?.id) }
	)

	const {
		data: metadataData,
		isLoading: isLoadingMetadata,
		isFetched,
		refetch: refetchMetadata,
	} = useMetadata(
		{
			id: fileInfo?.id ?? '',
		},
		{ enabled: Boolean(fileInfo?.id) }
	)
	const pendingMetadataActions = useIsMutating({
		mutationKey: ['classifyMetadata'],
		predicate: (mutation) =>
			(mutation.state.variables as { id?: string } | undefined)?.id === fileInfo?.id,
	})
	const classificationStatus = Number(metadataData?.data?.processingStatus)
	const isProcessing =
		pendingMetadataActions > 0 || classificationStatus === 0 || classificationStatus === 2

	useEffect(() => {
		if (isProcessing) onToggleEditMode(false)
	}, [isProcessing, onToggleEditMode])

	const { data: categoryData, refetch: refetchCategory } = useCategory(
		{
			id: fileInfo?.subcategory?.id ?? fileData?.data?.subcategory?.id ?? '',
		},
		{
			enabled: Boolean(
				isAuthenticated &&
				fileInfo?.id &&
				(fileInfo?.subcategory?.id || fileData?.data?.subcategory?.id)
			),
		}
	)

	const currentFileSubcategoryId =
		fileInfo?.subcategory?.id ?? fileData?.data?.subcategory?.id ?? ''

	useEffect(() => {
		if (!fileInfo?.id) return
		if (fileInfo?.metadata?.documentMetadata) {
			reset(fileInfo.metadata.documentMetadata)
		} else {
			reset({})
		}
	}, [fileInfo?.id, reset])

	useEffect(() => {
		if (
			!currentFileSubcategoryId ||
			!metadataData?.data?.values ||
			!categoryData?.data?.fields
		)
			return

		const metadataMap: Record<string, string> = {}
		metadataData.data.values.forEach((item) => {
			metadataMap[item.id] = item.value
		})

		categoryData.data.fields.forEach((field) => {
			if (!field.id) return

			const rawValue = metadataMap[field.id]
			const parsedValue = extractValue(rawValue, field.type, field.multiple)
			setValue(field.name, parsedValue)
		})
	}, [
		currentFileSubcategoryId,
		metadataData?.data?.values,
		categoryData?.data?.fields,
		setValue,
	])

	const onSubmit = async (data: DocumentMetadataForm) => {
		if (!fileInfo?.id || isProcessing) return
		const values = Object.entries(data)
			.filter(([key]) => key !== 'processingStatus')
			.map(([name, value]) => {
				const field = categoryData?.data?.fields.find((f) => f.name === name)
				if (field?.multiple && Array.isArray(value)) {
					return {
						id: field?.id || '',
						value: JSON.stringify(value),
					}
				}

				return {
					id: field?.id || '',
					value: String(value),
				}
			})
			.filter((item) => item.id)
		await updateMetadata(
			{
				data: {
					id: fileInfo.id,
					values,
				},
			},
			{
				onSuccess: () => {
					onToggleEditMode(false)
					reset()
				},
			}
		)
	}

	const renderField = (field: CategoryItemField, value: string) => {
		switch (field.type) {
			case 1:
				return (
					<Controller
						control={control}
						name={field.name}
						render={({ field: formField }) => (
							<Input
								defaultValue={value}
								wrapperClassName="w-full h-9"
								key={field.id}
								type="number"
								value={formField?.value}
								onChange={formField.onChange}
								max={field?.length || undefined}
							/>
						)}
					/>
				)
			case 2:
				if (field.multiple) {
					return (
						<Controller
							control={control}
							name={field.name}
							render={({ field: formField }) => (
								<MultiValueInput
									key={field.id}
									type="date"
									value={formField?.value || []}
									onChange={formField.onChange}
								/>
							)}
						/>
					)
				}
				return (
					<Controller
						control={control}
						name={field.name}
						render={({ field: formField }) => (
							<DatePicker
								key={field.id}
								value={
									formField?.value
										? dayjs(formField.value).isValid()
											? dayjs(formField.value).toDate()
											: null
										: null
								}
								onChange={(newValue) => {
									if (newValue) {
										formField.onChange(dayjs(newValue).toISOString())
									} else {
										formField.onChange('')
									}
								}}
								placeholder="Pick a date"
								className="w-full dark:text-text-main-dark dark:bg-background-dark"
							/>
						)}
					/>
				)
			case 3:
				return (
					<Controller
						name={field.name}
						control={control}
						render={({ field: formField }) => (
							<Checkbox
								onChange={(e) => formField?.onChange(e ? 'true' : 'false')}
								checked={String(formField?.value)?.toLowerCase() === 'true'}
							/>
						)}
					/>
				)
			default:
				if (field.multiple) {
					return (
						<Controller
							name={field.name}
							control={control}
							render={({ field: formField }) => (
								<MultiValueInput key={field.id} {...formField} />
							)}
						/>
					)
				}
				return (
					<Controller
						name={field.name}
						control={control}
						render={({ field: formField }) => (
							<Input
								key={field.id}
								value={formField?.value}
								onChange={formField.onChange}
								wrapperClassName="w-full h-9"
								defaultValue={value}
								max={field?.length || undefined}
							/>
						)}
					/>
				)
		}
	}

	const renderValue = (value: string) => {
		if (!value || value === 'null') return '-'

		try {
			const parsed = JSON.parse(value)

			if (Array.isArray(parsed)) {
				if (!parsed.length) return '-'

				const areAllDates = parsed.every((v) => dayjs(v).isValid())

				if (areAllDates) {
					return parsed
						.map((date) => formatDate(String(date)))
						.join('; ')
				}

				return parsed.join(', ')
			}

			if (typeof parsed === 'boolean') {
				return parsed ? 'True' : 'False'
			}

			return String(parsed)
		} catch {
			const lower = value.toLowerCase()
			if (lower === 'true') return 'True'
			if (lower === 'false') return 'False'

			if (dayjs(value).isValid()) {
				return formatDate(value)
			}

			return value
		}
	}

	const fileFields = useMemo(() => {
		if (!currentFileSubcategoryId || !categoryData?.data?.fields) {
			return []
		}
		return categoryData.data.fields
	}, [currentFileSubcategoryId, categoryData?.data?.fields])

	useEffect(() => {
		// Poll only for status 0 (Pending) and 2 (Processing Document)
		if (
			!fileInfo?.id ||
			(classificationStatus !== 0 && classificationStatus !== 2)
		)
			return

		const interval = setInterval(() => {
			refetchMetadata()
		}, 5000)

		return () => clearInterval(interval)
	}, [classificationStatus, fileInfo?.id, refetchMetadata])

	return (
		<>
			{!isLoadingMetadata &&
				isFetched &&
				(isProcessing ? (
					<div className="h-full gap-2 flex items-center justify-center mt-10">
						<Loader />
						{processingStatuses[classificationStatus === 0 ? 0 : 2]}
					</div>
				) : (
					classificationStatus === 3 && (
						<div className="flex items-center justify-center gap-2 flex-col mt-10">
							<p className="text-sm text-center italic text-text-main flex items-center justify-center gap-1 bg-red-50 p-5 mb-2 rounded-md">
								<ErrorOutline className="fill-negative" />
								{processingStatuses[classificationStatus]}
							</p>
							<Button
								loading={isClassifying || isExacting}
								label="Classify again"
								className="min-w-28 flex items-center justify-center"
								leftIcon={<Refresh />}
								onClick={() => {
									classifyFile(
										{ id: fileInfo?.id ?? '', withoutToast: true },
										{
											onSuccess: () => {
												if (
													isAuthenticated &&
													fileInfo?.id &&
													(fileInfo?.subcategory?.id ||
														fileData?.data?.subcategory?.id)
												) {
													refetchCategory()
												}
											},
										}
									)
								}}
							/>
						</div>
					)
				))}
			{isLoadingMetadata || isFileLoading || !isFetched ? (
				<div className="h-full flex items-center justify-center">
					<Loader />
				</div>
			) : (
				isFetched &&
				!isProcessing &&
				classificationStatus === 1 &&
				(!(metadataData?.data?.values?.length ?? 0) && !currentFileSubcategoryId ? (
					<div className="flex items-center justify-center gap-2 flex-col mt-10">
						<p className="text-sm text-center text-text-secondary dark:text-text-table-dark bg-background-light dark:bg-background-light-dark p-5 mb-2 rounded-md">
							File has no category or subcategory
						</p>
						<Button
							loading={isClassifying || isExacting}
							label="Reclassify"
							className="min-w-28 flex items-center justify-center"
							leftIcon={<Refresh />}
							onClick={() => {
								classifyFile(
									{ id: fileInfo?.id ?? '', withoutToast: true },
									{
										onSuccess: () => {
											if (isAuthenticated && fileInfo?.id && currentFileSubcategoryId) {
												refetchCategory()
											}
											refetchMetadata()
										},
									}
								)
							}}
						/>
					</div>
				) : (
					<form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
						<div className="flex items-center justify-between gap-2 h-fit mb-5">
							<Input
								wrapperClassName="flex-1 h-9"
								placeholder="Search"
								leftIcon={<Search className="fill-stroke-light" />}
								rightIcon={
									searchQuery?.trim()?.length ? (
										<Cancel
											className="fill-stroke-light w-4 hover:fill-stroke-light-hover cursor-pointer"
											onClick={() => setSearchQuery('')}
										/>
									) : null
								}
								onChange={(e) =>
									setSearchQuery(String(e?.currentTarget?.value ?? ''))
								}
								value={searchQuery}
							/>
							{isEditMode ? (
								<div className="flex items-center justify-end gap-2">
									<IconButton
										variant="secondary"
										className="h-9 w-9"
										icon={<Close />}
										onClick={() => onToggleEditMode(false)}
									/>
									<IconButton
										className="h-9 w-9"
										icon={<Done className="fill-white" />}
										disabled={!isDirty}
										onClick={handleSubmit(onSubmit)}
										loading={isUpdating}
									/>
								</div>
							) : (
								<IconButton
									variant="secondary"
									className="h-9 w-9"
									icon={<EditOutlined />}
									onClick={() => onToggleEditMode((prev) => !prev)}
									disabled={!fileFields?.length}
								/>
							)}
						</div>
						{isEditMode ? (
							<div className="flex flex-col items-start justify-start gap-4">
								{fileFields?.length ? (
									fileFields
										?.filter(({ name, friendlyName }) =>
											`${name}${friendlyName}`
												?.toLocaleLowerCase()
												?.trim()
												?.includes(searchQuery?.toLocaleLowerCase()?.trim())
										)
										?.map((field) => {
											const metadataValue =
												metadataData?.data?.values.find(
													(item) => item.id === field.id
												)?.value || ''
											return (
												<div
													key={field.id}
													className="w-full gap-1 items-start flex flex-col"
												>
													<p className="w-full capitalize text-text-secondary text-sm">
														{field.friendlyName || field.name}
													</p>
													{renderField(field, metadataValue)}
												</div>
											)
										})
								) : (
									<p className="text-text-secondary text-center text-sm italic w-full">
										No fields for this category
									</p>
								)}
							</div>
						) : (
							<div className="border border-stroke-light dark:border-stroke-light-dark  rounded-lg overflow-hidden">
								{fileFields
									?.filter(({ name, friendlyName }) =>
										`${name}${friendlyName}`
											?.toLocaleLowerCase()
											?.trim()
											?.includes(searchQuery?.toLocaleLowerCase()?.trim())
									)
									?.map((field, index) => {
										const metadataValue =
											metadataData?.data?.values.find(
												(item) => item.id === field.id
											)?.value || ''
										return (
											<div
												key={field.id}
												className="flex border-b border-stroke-light dark:border-stroke-light-dark last:border-b-0"
											>
												<div className="w-[180px] border-r border-stroke-light dark:border-stroke-light-dark px-3 py-2.5 min-h-[48px] box-border flex items-start justify-start">
													<p className="text-text-main dark:text-text-main-dark text-sm whitespace-nowrap font-bold pt-0.5">
														{field.friendlyName || field.name}
													</p>
												</div>
												<div className="flex-1 px-3 py-2.5 min-h-[48px] box-border flex items-center justify-start">
													<p className="text-text-main dark:text-text-main-dark text-lg">
														{renderValue(metadataValue)}
													</p>
												</div>
											</div>
										)
									})}
							</div>
						)}
					</form>
				))
			)}
		</>
	)
}

const extractValue = (
	originalValue: string | null | undefined,
	fieldType: number,
	multiple?: boolean
) => {
	const isEmpty =
		!originalValue || originalValue === 'null' || originalValue === ''

	if (multiple) {
		if (isEmpty) return []

		const array = tryParseArray(originalValue)
		const values = array ?? [originalValue]

		return values
			.map((val) => normalizeValue(val, fieldType))
			.filter((val) => val !== null)
	} else {
		if (isEmpty) return null

		const array = tryParseArray(originalValue)
		const val = array ? array[0] : originalValue

		return normalizeValue(val, fieldType)
	}
}

const tryParseArray = (value: string) => {
	try {
		const parsed = JSON.parse(value)
		return Array.isArray(parsed) ? parsed : null
	} catch {
		return null
	}
}

const normalizeValue = (value: any, fieldType: number) => {
	switch (fieldType) {
		case 0: {
			// TEXT
			return String(value)
		}
		case 1: {
			// NUMBER
			const num = Number(value)
			return isNaN(num) ? 0 : num
		}
		case 2: {
			// DATE
			if (!value) return null
			const date = dayjs(value)
			return date.isValid() ? date.toISOString() : null
		}
		case 3: {
			// BOOLEAN
			return ['true', '1'].includes(String(value)?.toLowerCase())
		}
		default: {
			return value
		}
	}
}

export default AiFormTab

// <Button
// 			loading={isClassifying || isExacting}
// 			label={classificationStatus === 3 ? 'Classify again' : 'Extract again'}
// 			className="min-w-28 flex items-center justify-center"
// 			leftIcon={<Refresh />}
// 			onClick={() =>
// 				// classificationStatus === 3
// 				// 	? classifyFile({ id: fileInfo?.id ?? ''})
// 				extractMetadata({ id: fileInfo?.id ?? ''})
// 			}
// 		/>
